import {
  ComboboxPopover,
  type Option,
} from '@/components/ui/combobox/combobox-popover';
import type { Deal } from '@/models/registry';
import type { Payment } from '@/models/types';
import { BanknoteArrowDown, Building2, PiggyBank } from 'lucide-react';
import { useMemo, useState } from 'react';
import { PaymentTypeBadge } from './payment-type-badge';

// down_payment, full_amount
export const PAYMENT_TYPE_OPTIONS_MAP: Option<Payment['type']>[] = [
  {
    label: 'Down Payment',
    value: 'down_payment',
    icon: <BanknoteArrowDown />,
    hotkey: '1',
    abbreviation: 'down',
  },
  {
    label: 'Funding Amount',
    value: 'funding_amount',
    icon: <Building2 />,
    hotkey: '2',
    abbreviation: 'fund',
  },
  {
    label: 'Full Amount',
    value: 'full_amount',
    icon: <PiggyBank />,
    hotkey: '3',
    abbreviation: 'full',
  },
];

type PaymentTypeSelectorCombobox = {
  value: Payment['type'];
  setValue: (value: Payment['type']) => void;
};

export function PaymentTypeSelectorCombobox({
  value,
  setValue,
}: PaymentTypeSelectorCombobox) {
  const [open, setOpen] = useState(false);
  const handlePaymentTypeChange = (
    newPaymentType: Deal['payments'][0]['type'],
  ) => {
    setValue(newPaymentType);
  };

  const currentOption = useMemo(() => {
    return PAYMENT_TYPE_OPTIONS_MAP.find((option) => option.value === value);
  }, [value]);

  return (
    <ComboboxPopover
      open={open}
      onOpenChange={setOpen}
      endAdornment={null}
      placeholder="Set payment type..."
      onChange={handlePaymentTypeChange}
      emptyMessage="No payment type found"
      currentOption={currentOption}
      options={PAYMENT_TYPE_OPTIONS_MAP}
      // side={side}
      align="start"
      onKeyDown={(e) => {
        const keyPressed = e.key;

        const option = PAYMENT_TYPE_OPTIONS_MAP.find(
          (option) => option.hotkey === keyPressed,
        );

        if (option) {
          handlePaymentTypeChange(option.value);
          e.preventDefault();
          e.stopPropagation();
          setOpen(false);
        }
      }}
      stayOpenAfterSelection={false}
    >
      <div
      // DEV NOTE: without this div, combobox-popover won't work
      // that is, clicks will not open the combobox
      // I think the reason is children node needs to be able to receive a ref
      // in order to detect a click event, which makes it open
      // thus so this div is a workaround
      >
        <PaymentTypeBadge value={value} />
      </div>
    </ComboboxPopover>
  );
}
